import React, {useState, useRef} from "react"
import {LoginFormData, FormErrors} from "../../types/auth"
import {validateEmail} from "../../utils/validations"

interface EmailFormProps {
	onSubmit: (email: string) => Promise<void>
	isLoading: boolean
}

export const EmailForm: React.FC<EmailFormProps> = ({onSubmit, isLoading}) => {
	const [formData, setFormData] = useState<LoginFormData>({email: ""})
	const [errors, setErrors] = useState<FormErrors>({})
	const submittingRef = useRef(false)

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (submittingRef.current) {
			return
		}

		const emailError = validateEmail(formData.email)
		if (emailError) {
			setErrors({email: emailError})
			return
		}

		setErrors({})
		submittingRef.current = true

		try {
			await onSubmit(formData.email)
		} catch (error) {
			setErrors({general: error instanceof Error ? error.message : "An error occurred"})
		} finally {
			setTimeout(() => {
				submittingRef.current = false
			}, 2000)
		}
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setFormData({email: value})

		submittingRef.current = false

		if (errors.email && value) {
			const emailError = validateEmail(value)
			if (!emailError) {
				setErrors((prev) => ({...prev, email: undefined}))
			}
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div>
				<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
					Email Address
				</label>
				<input
					id="email"
					type="email"
					value={formData.email}
					onChange={handleEmailChange}
					disabled={isLoading}
					className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
						errors.email ? "border-red-500" : "border-gray-300"
					} ${isLoading ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
					placeholder="Enter your email address"
				/>
				{errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
			</div>

			{errors.general && (
				<div className="p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-600">{errors.general}</p>
				</div>
			)}

			<button
				type="submit"
				disabled={isLoading || !formData.email || submittingRef.current}
				className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
				{isLoading ? "Sending..." : "Send OTP Code"}
			</button>
		</form>
	)
}
